"use client";

import {
  ModalContent,
  ModalFooter,
  ModalHeader,
  PaginationDot,
  PortalModal,
  usePortalContext,
} from "@cloai/uikit";
import { FC, useCallback, useRef, useState } from "react";

import { useUndressContext } from "@/providers";
import { cn } from "@/utils/cn";

import { StorylineImage } from "./StorylineImage";
import { StorylineNavigation } from "./StorylineNavigation";
import { useStorylineData } from "./useStorylineData";
import { PORTALS } from ".constants/portals";

/**
 * Компонент модального окна для отображения результатов сторилайна
 *
 * @description
 * Отображает последовательность изображений с соответствующими текстами.
 * Поддерживает навигацию между изображениями и автоматически закрывается
 * после просмотра последнего изображения.
 *
 * @example
 * ```tsx
 * <StorylineResultPortal />
 * ```
 */
export const StorylineResultPortal: FC = () => {
  const { closePortal } = usePortalContext();
  const { undressings } = useUndressContext();

  const refText = useRef<HTMLDivElement>(null);

  // Получаем данные для отображения
  const { images, texts } = useStorylineData(undressings);

  // Управление активным слайдом
  const [active, setActive] = useState(0);
  const limit = Math.max(0, Number(undressings?.length) - 1);

  // Обработчики навигации
  const handleScrollText = useCallback(() => {
    if (refText.current) {
      refText.current.scrollTo(0, 0);
    }
  }, []);

  const handleClose = useCallback(() => {
    closePortal();
    setTimeout(() => {
      setActive(0);
      handleScrollText();
    }, 200);
  }, [closePortal, handleScrollText]);

  const onBack = useCallback(() => {
    setActive((prev) => prev - 1);
    handleScrollText();
  }, [handleScrollText]);

  const onNext = useCallback(() => {
    active === limit ? handleClose() : setActive((prev) => prev + 1);
    handleScrollText();
  }, [active, handleClose, handleScrollText, limit]);

  return (
    <PortalModal portalId={PORTALS.STORYLINE_RESULT} onClose={handleClose}>
      <ModalHeader>
        <div
          className={cn(
            "relative aspect-square overflow-hidden rounded-md bg-card",
            "sm:-mx-popup-padding-x sm:-mt-popup-padding-y sm:rounded-t-popup sm:rounded-b-none",
          )}
        >
          {images.map((image, index) => (
            <StorylineImage
              key={index}
              src={image ?? ""}
              isShow={index === active}
            />
          ))}
        </div>

        <div className="flex items-center justify-center gap-2">
          {images.map((_item, index) => (
            <PaginationDot key={index} active={active === index} />
          ))}
        </div>
      </ModalHeader>

      <ModalContent className="h-40" ref={refText}>
        <span className="text-balance whitespace-pre-line text-popup-foreground">
          {texts[active]}
        </span>
      </ModalContent>

      <ModalFooter>
        <StorylineNavigation
          active={active}
          limit={limit}
          onBackAction={onBack}
          onNextAction={onNext}
        />
      </ModalFooter>
    </PortalModal>
  );
};
